/* eslint-disable no-console */
import React, { useCallback, useState } from "react";
import { Container, Grid, Typography } from "@material-ui/core";
import {
	DataGridPro,
	GridColumns,
	GridToolbarColumnsButton,
	GridToolbarDensitySelector,
	GridToolbarExport,
	GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import PageBreadcrumbs from "../components/Breadcrumbs/PageBreadcrumbs";
import { breadcrumbNode } from "../components/Breadcrumbs/PageBreadcrumbs.d";
import { useBatchUpdateInventory } from "../api/replenishmentApi";
import { Box, Button, Tooltip, Snackbar, Alert } from "@mui/material";
import { DownloadOutlined, FileUploadOutlined } from "@mui/icons-material";

interface InventoryUpdateRow {
	id: string;
	sku: string;
	locationId: number;
	minQty: number | null;
	maxQty: number | null;
}

const InventoryUpdates: React.FC = () => {
	const breadcrumbNodes: breadcrumbNode[] = [{ name: "Update Min / Max" }];

	const [rows, setRows] = useState<InventoryUpdateRow[]>([]);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" } | null>(
		null
	);
	const [{ loading: isSaving }, batchUpdateInventory] = useBatchUpdateInventory();

	const columns: GridColumns = [
		{ field: "sku", headerName: "SKU", width: 150, editable: false },
		{ field: "locationId", headerName: "Location ID", type: "number", width: 130, editable: false },
		{ field: "minQty", headerName: "Min", type: "number", width: 100, editable: true },
		{ field: "maxQty", headerName: "Max", type: "number", width: 100, editable: true },
	];

	const handleDownloadTemplate = useCallback(() => {
		const csvContent = "Sku,LocationId,Min,Max\n";
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", "inventory_template.csv");
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}, []);

	const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		console.log(event);
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const text = e.target?.result as string;
				const lines = text.split("\n");
				const headers = lines[0]
					.toLowerCase()
					.split(",")
					.map((h) => h.trim());

				// Validate headers
				const expectedHeaders = ["sku", "locationid", "min", "max"];
				const isValidHeader = expectedHeaders.every((header) => headers.includes(header));

				if (!isValidHeader) {
					setSnackbar({
						open: true,
						message: "Invalid CSV format. Expected headers: Sku,LocationId,Min,Max",
						severity: "error",
					});
					return;
				}

				const newRows: InventoryUpdateRow[] = [];
				for (let i = 1; i < lines.length; i++) {
					const line = lines[i].trim();
					if (line) {
						const values = line.split(",").map((v) => v.trim());
						if (values.length >= 4) {
							const row: InventoryUpdateRow = {
								id: `${values[0]}-${values[1]}`,
								sku: values[0],
								locationId: parseInt(values[1]),
								minQty: values[2] ? parseInt(values[2]) : null,
								maxQty: values[3] ? parseInt(values[3]) : null,
							};
							newRows.push(row);
						}
					}
				}
				setRows(newRows);
			};
			reader.readAsText(file);
		}
		// Reset the input value so the same file can be uploaded again
		event.target.value = "";
	}, []);

	const handleSave = useCallback(async () => {
		try {
			const updateRequests = rows.map((row) => ({
				sku: row.sku,
				locationId: row.locationId,
				minQty: row.minQty,
				maxQty: row.maxQty,
			}));

			await batchUpdateInventory(updateRequests)
				.then(() => {
					setSnackbar({
						open: true,
						message: "Inventory updates saved successfully!",
						severity: "success",
					});
				})
				.catch((error) => {
					console.log(`${error.message} | ${error?.response?.data}`);
					setSnackbar({
						open: true,
						message: error?.message
							? `Error saving inventory updates: ${error.message} | ${error?.response?.data}`
							: "Error saving inventory updates. Please try again.",
						severity: "error",
					});
				});
		} catch (error: any) {
			// Do nothing
		}
	}, [rows, batchUpdateInventory]);

	const handleRowEdit = useCallback((updatedRow: InventoryUpdateRow) => {
		setRows((prevRows) => prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
		return updatedRow;
	}, []);

	const CustomToolbar = () => {
		return (
			<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<GridToolbarColumnsButton />
					<GridToolbarFilterButton />
					<GridToolbarDensitySelector />
					<GridToolbarExport />
					<Tooltip title="Download Import Template">
						<Button size="small" startIcon={<DownloadOutlined />} onClick={handleDownloadTemplate} sx={{ height: 36 }}>
							Template
						</Button>
					</Tooltip>
					<input
						id="inventory-import-input"
						type="file"
						accept=".csv"
						style={{ display: "none" }}
						onChange={handleImport}
					/>
					<label htmlFor="inventory-import-input">
						<Button size="small" startIcon={<FileUploadOutlined />} component="span" sx={{ height: 36 }}>
							Import
						</Button>
					</label>
					{rows.length > 0 && (
						<Button color="primary" onClick={() => handleSave()} disabled={isSaving} size="small" sx={{ height: 36 }}>
							{isSaving ? "Saving..." : "Save All"}
						</Button>
					)}
				</Box>
			</Box>
		);
	};

	return (
		<Container maxWidth={false}>
			<Grid container spacing={6}>
				<Grid container item direction="row">
					<Grid item xs={12} sm={8}>
						<PageBreadcrumbs nodes={breadcrumbNodes} />
					</Grid>
				</Grid>
				<Grid container>
					<Typography variant="h4">Inventory Updates</Typography>
				</Grid>
				<Grid container>
					<Typography>Upload a CSV file with inventory updates or download the template to get started.</Typography>
				</Grid>
				<Grid item xs={12}>
					<DataGridPro
						autoHeight
						density="standard"
						rows={rows}
						columns={columns}
						paginationMode="client"
						rowsPerPageOptions={[25, 50, 100]}
						pagination
						processRowUpdate={handleRowEdit}
						onProcessRowUpdateError={(error) => console.error(error)}
						components={{
							Toolbar: CustomToolbar,
						}}
						initialState={{
							pagination: {
								pageSize: 25,
							},
							sorting: {
								sortModel: [{ field: "sku", sort: "asc" }],
							},
						}}
					/>
				</Grid>
			</Grid>
			<Snackbar
				open={!!snackbar?.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
					severity={snackbar?.severity}
					sx={{
						width: "100%",
						bgcolor: snackbar?.severity === "success" ? "success.main" : "error.main",
						color: "#fff",
					}}
				>
					{snackbar?.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default InventoryUpdates;
